import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, CreditCard, CheckCircle, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const STATUS_STYLE = {
  paid:    'bg-emerald-100 text-emerald-700',
  partial: 'bg-amber-100 text-amber-700',
  unpaid:  'bg-red-100 text-red-700',
  overdue: 'bg-red-100 text-red-700',
};
const STATUS_ICON = {
  paid:    <CheckCircle className="w-3.5 h-3.5" />,
  partial: <Clock className="w-3.5 h-3.5" />,
  unpaid:  <CreditCard className="w-3.5 h-3.5" />,
  overdue: <AlertCircle className="w-3.5 h-3.5" />,
};

function loadPaystackScript() {
  return new Promise((resolve) => {
    if (window.PaystackPop) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
}

function loadFlutterwaveScript() {
  return new Promise((resolve) => {
    if (window.FlutterwaveCheckout) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.flutterwave.com/v3.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
}

export default function ParentFeesPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paySettings, setPaySettings] = useState(null);
  const [paying, setPaying] = useState('');

  useEffect(() => {
    Promise.allSettled([
      api.get('/fees/parent/invoices'),
      api.get('/fees/school-payment-public'),
    ]).then(([feeRes, settingsRes]) => {
      if (feeRes.status === 'fulfilled' && feeRes.value?.success) setData(feeRes.value.data || []);
      else if (feeRes.status === 'rejected') setError(feeRes.reason?.detail || 'Failed to load fees');
      if (settingsRes.status === 'fulfilled' && settingsRes.value?.success) setPaySettings(settingsRes.value.data);
    }).finally(() => setLoading(false));
  }, []);

  const reloadInvoices = async () => {
    try {
      const res = await api.get('/fees/parent/invoices');
      if (res.success) setData(res.data || []);
    } catch {}
  };

  const handlePayNow = async (invoice) => {
    if (!paySettings?.configured) {
      toast.error('Online payment is not yet configured by this school. Please contact the school bursar.');
      return;
    }
    setPaying(invoice.id);
    try {
      const res = await api.post('/fees/initialize-payment', {
        invoice_id: invoice.id,
        gateway: paySettings.gateway,
        callback_url: window.location.origin + '/parent/fees',
      });
      const pdata = res.data;

      if (pdata.gateway === 'paystack') {
        const loaded = await loadPaystackScript();
        if (!loaded) { toast.error('Failed to load Paystack'); setPaying(''); return; }
        const handler = window.PaystackPop.setup({
          key: pdata.public_key,
          reference: pdata.reference,
          onSuccess: async (response) => {
            toast.loading('Verifying payment...');
            try {
              const verRes = await api.get(`/fees/verify-payment?reference=${response.reference}&gateway=paystack`);
              if (verRes.data?.status === 'paid') {
                toast.success('Payment successful! Invoice updated.');
                reloadInvoices();
              } else {
                toast.error('Payment verification pending. Refresh to update status.');
              }
            } catch { toast.error('Verification failed. Contact school if charged.'); }
            setPaying('');
          },
          onCancel: () => { toast.info('Payment cancelled'); setPaying(''); },
        });
        handler.openIframe();
      } else if (pdata.gateway === 'flutterwave') {
        const loaded = await loadFlutterwaveScript();
        if (!loaded) { toast.error('Failed to load Flutterwave'); setPaying(''); return; }
        window.FlutterwaveCheckout({
          public_key: pdata.public_key,
          tx_ref: pdata.reference,
          amount: invoice.balance || invoice.amount,
          currency: 'NGN',
          customer: { email: 'parent@school.ng', name: 'Parent' },
          customizations: { title: invoice.description || 'School Fee', description: 'Fee Payment' },
          callback: async (payResponse) => {
            try {
              const verRes = await api.get(`/fees/verify-payment?reference=${pdata.reference}&gateway=flutterwave&transaction_id=${payResponse.transaction_id}`);
              if (verRes.data?.status === 'paid') {
                toast.success('Payment successful! Invoice updated.');
                reloadInvoices();
              } else {
                toast.error('Verification pending.');
              }
            } catch { toast.error('Verification failed.'); }
            setPaying('');
          },
          onclose: () => { toast.info('Payment cancelled'); setPaying(''); },
        });
      }
    } catch (err) {
      toast.error(err?.detail || 'Payment initialization failed');
      setPaying('');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-4 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex gap-3">
        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
        <p className="text-red-700 text-sm">{error}</p>
      </div>
    </div>
  );

  const totalOutstanding = data.reduce((s, c) => s + (c.total_outstanding || 0), 0);
  const gatewayName = paySettings?.gateway === 'flutterwave' ? 'Flutterwave' : 'Paystack';

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto space-y-5" data-testid="parent-fees-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black text-slate-900">School Fees</h1>
        <p className="text-slate-500 text-sm mt-0.5">Manage fee invoices for your children across all schools</p>
      </div>

      {/* Outstanding Banner */}
      {totalOutstanding > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-2xl flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-amber-800 text-sm">Outstanding Balance</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Total due: <strong>₦{totalOutstanding.toLocaleString()}</strong>
              {paySettings?.configured
                ? ` · Pay online via ${gatewayName}`
                : ' · Contact school bursar to make payment'}
            </p>
          </div>
        </div>
      )}

      {data.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-100 py-16 text-center shadow-sm">
          <CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-200" />
          <p className="font-bold text-slate-500">No fee records found</p>
          <p className="text-sm text-slate-400 mt-1">No invoices have been issued yet.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Group by school */}
          {Object.entries(
            data.reduce((acc, child) => {
              const key = child.school_name || child.student?.school_name || 'Unknown School';
              if (!acc[key]) acc[key] = [];
              acc[key].push(child);
              return acc;
            }, {})
          ).map(([schoolName, schoolChildren]) => (
            <div key={schoolName}>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-px flex-1 bg-slate-100" />
                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest px-3 bg-slate-50 rounded-full py-1 border border-slate-100">
                  {schoolName}
                </span>
                <div className="h-px flex-1 bg-slate-100" />
              </div>
              <div className="space-y-4">
                {schoolChildren.map((child, ci) => (
                  <div key={ci} className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm">
                    {/* Child Header */}
                    <div className="bg-blue-50 border-b border-blue-100 px-5 py-3.5 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-black text-[#2563eb]">{child.student?.first_name?.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="font-bold text-slate-800 text-sm">{child.student?.first_name} {child.student?.last_name}</p>
                          <p className="text-xs text-slate-500">{child.student?.class_name} · {child.student?.admission_number}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {child.total_outstanding > 0
                          ? <p className="text-sm font-bold text-red-600">₦{child.total_outstanding.toLocaleString()} outstanding</p>
                          : <p className="text-sm font-bold text-emerald-600 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> All paid</p>
                        }
                      </div>
                    </div>

                    {/* Invoices */}
                    {(child.invoices || []).length === 0 ? (
                      <div className="px-5 py-8 text-center text-sm text-slate-400">No invoices issued</div>
                    ) : (
                      <div className="divide-y divide-slate-50">
                        {child.invoices.map((inv, ii) => (
                          <div key={ii} className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-slate-700 text-sm truncate">{inv.description || inv.fee_name || 'School Fees'}</p>
                              <div className="flex items-center gap-3 mt-0.5">
                                {inv.term_name && <p className="text-xs text-slate-400">{inv.term_name}</p>}
                                {inv.due_date && <p className="text-xs text-slate-400">Due: {inv.due_date}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                              <div className="text-right">
                                <p className="text-sm font-bold text-slate-800">₦{(inv.amount || 0).toLocaleString()}</p>
                                {inv.paid_amount > 0 && <p className="text-xs text-emerald-600">Paid: ₦{inv.paid_amount.toLocaleString()}</p>}
                                {inv.balance > 0 && <p className="text-xs text-red-600">Due: ₦{inv.balance.toLocaleString()}</p>}
                              </div>
                              <span className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_STYLE[inv.status] || 'bg-slate-100 text-slate-600'}`}>
                                {STATUS_ICON[inv.status] || <Clock className="w-3.5 h-3.5" />}
                                {inv.status?.charAt(0).toUpperCase() + inv.status?.slice(1)}
                              </span>
                              {(inv.status === 'unpaid' || inv.status === 'partial' || inv.status === 'overdue') && (
                                <button
                                  onClick={() => handlePayNow(inv)}
                                  disabled={paying === inv.id}
                                  className="flex items-center gap-1.5 bg-[#2563eb] hover:bg-[#1d58d4] text-white text-xs font-bold px-3 py-2 rounded-xl disabled:opacity-50 transition-colors whitespace-nowrap shadow-sm"
                                  data-testid={`pay-now-${inv.id}`}
                                >
                                  {paying === inv.id
                                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Processing...</>
                                    : <><CreditCard className="w-3 h-3" /> Pay Now</>}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
